/**
 * Global Request Manager for handling API request cancellation
 * This ensures no duplicate requests across the entire application
 */

class RequestManager {
  private activeRequests: Map<string, AbortController> = new Map();
  private requestTimeouts: Map<string, NodeJS.Timeout> = new Map();

  /**
   * Create or get an AbortController for a specific request key
   * If a request with the same key exists, it will be cancelled
   */
  createRequest(key: string, timeoutMs: number = 60000): AbortController {
    // Extract endpoint from key to cancel similar requests
    const endpoint = this.extractEndpoint(key);

    // Never cancel in-flight auth/session bootstrap calls — multiple callers (App, PrivateRoute,
    // Login) can start POST auth/me or GET company at the same time; cancelling one breaks login.
    const skipDedupCancel =
      endpoint === "auth/me" ||
      endpoint === "auth/login" ||
      endpoint === "auth/refresh" ||
      endpoint === "company";

    if (!skipDedupCancel) {
      // Cancel ALL previous requests to the same endpoint
      this.cancelRequestsByPattern(endpoint);
    }

    // Create new AbortController
    const abortController = new AbortController();
    this.activeRequests.set(key, abortController);

    // Set timeout to auto-cancel after specified time
    const timeout = setTimeout(() => {
      this.cancelRequest(key);
    }, timeoutMs);
    
    this.requestTimeouts.set(key, timeout);

    return abortController;
  }

  /**
   * Extract endpoint from request key
   */
  private extractEndpoint(key: string): string {
    // Key format: "METHOD:endpoint{params}"
    const match = key.match(/^[A-Z]+:([^{]+)/);
    return match ? match[1] : key;
  }

  /**
   * Cancel a specific request by key
   */
  cancelRequest(key: string): void {
    const abortController = this.activeRequests.get(key);
    if (abortController) {
      abortController.abort();
      this.activeRequests.delete(key);
    }

    const timeout = this.requestTimeouts.get(key);
    if (timeout) {
      clearTimeout(timeout);
      this.requestTimeouts.delete(key);
    }
  }

  /**
   * Complete a request (clean up)
   */
  completeRequest(key: string): void {
    this.activeRequests.delete(key);
    
    const timeout = this.requestTimeouts.get(key);
    if (timeout) {
      clearTimeout(timeout);
      this.requestTimeouts.delete(key);
    }
  }

  /**
   * Cancel all active requests
   */
  cancelAllRequests(): void {
    
    this.activeRequests.forEach((controller, key) => {
      controller.abort();
    });
    this.activeRequests.clear();

    this.requestTimeouts.forEach((timeout) => {
      clearTimeout(timeout);
    });
    this.requestTimeouts.clear();
  }

  /**
   * Cancel requests by pattern (e.g., only search requests)
   */
  cancelRequestsByPattern(pattern: string): void {
    const keysToCancel = Array.from(this.activeRequests.keys()).filter(key => {
      // Use more specific pattern matching to avoid false positives
      if (pattern === 'customers/search') {
        return key.includes('customers/search');
      }
      if (pattern === 'products/pos/filter') {
        return key.includes('products/pos/filter');
      }
      // Default behavior for other patterns
      return key.includes(pattern);
    });
    
    
    keysToCancel.forEach(key => {
      this.cancelRequest(key);
    });
  }

  /**
   * Cancel requests by pattern but exclude certain patterns
   */
  cancelRequestsByPatternExcluding(pattern: string, excludePatterns: string[]): void {
    const keysToCancel = Array.from(this.activeRequests.keys()).filter(key => {
      const matchesPattern = key.includes(pattern);
      const shouldExclude = excludePatterns.some(exclude => key.includes(exclude));
      return matchesPattern && !shouldExclude;
    });
    
    
    keysToCancel.forEach(key => {
      this.cancelRequest(key);
    });
  }

  /**
   * Get the signal for a specific request
   */
  getSignal(key: string): AbortSignal | undefined {
    return this.activeRequests.get(key)?.signal;
  }

  /**
   * Check if a request is active
   */
  isRequestActive(key: string): boolean {
    return this.activeRequests.has(key);
  }

  /**
   * Get all active request keys
   */
  getActiveRequestKeys(): string[] {
    return Array.from(this.activeRequests.keys());
  }
}

// Export singleton instance
export const requestManager = new RequestManager();

// Helper function to create request keys
export const createRequestKey = (endpoint: string, params?: any): string => {
  const paramString = params ? JSON.stringify(params) : '';
  return `${endpoint}${paramString}`;
};

// Helper function for API calls with automatic cancellation
export const makeApiCall = async <T>(
  endpoint: string,
  apiCall: (signal: AbortSignal) => Promise<T>,
  params?: any,
  timeoutMs?: number
): Promise<T> => {
  const requestKey = createRequestKey(endpoint, params);
  const abortController = requestManager.createRequest(requestKey, timeoutMs);

  try {
    const result = await apiCall(abortController.signal);
    requestManager.completeRequest(requestKey);
    return result;
  } catch (error: any) {
    // Don't log cancellation errors - they're expected behavior
    if (error.name === 'AbortError' || error.name === 'CanceledError') {
      // Silent - these are normal when requests are canceled
    } else {
      // Hide error logs in production, especially for 404/500 errors
      // Check multiple ways to determine production mode
      const nodeEnv = import.meta.env.VITE_NODE_ENV;
      const reactAppEnv = import.meta.env.VITE_ENV;
      const isProduction = nodeEnv === 'production' || reactAppEnv === 'production' || nodeEnv !== 'development';
      console.log('isProduction', isProduction);
      // Get error status from response
      const errorStatus = error?.response?.status || error?.status;
      
      // In production, completely hide 404 and 500 errors
      // Also check if error message contains 404/500
      const is404Or500 = errorStatus === 404 || errorStatus === 500 || errorStatus === 401 ||
                        error?.message?.includes('404') || 
                        error?.message?.includes('500') ||
                        error?.code === 'ERR_BAD_REQUEST';
      
      if (isProduction && is404Or500) {
        // Silent - don't log 404/500 errors in production
        // Just throw the error without logging
      } else if (!isProduction) {
        // Only log in non-production environments
        console.error(`❌ Request failed: ${requestKey}`, error);
      }
    }
    throw error;
  }
};
